// Floorplan renderer contracts (Phase 2a).
//
// The renderer is a pure function of three contracts:
//   model        — the building data (what exists)
//   viewState    — camera + selection + display options (how you're looking)
//   editingState — transient mid-edit data (what you're doing right now)
// plus `io` — explicit I/O channels (canvas/measurement refs, a couple of
// injected draw closures, and timing constants) that are not "data".
//
// `buildFloorplanContracts` (flat -> contracts) and `flattenFloorplanContracts`
// (contracts -> flat) are exact inverses (guarded by a roundtrip test). The
// coordinator builds the contracts; the renderer flattens them back into the
// exact local names its verbatim draw code uses — so no value can be lost or
// misplaced, and `drawFloorPlan` stays byte-identical.

export function buildFloorplanContracts(s) {
  return {
    model: {
      structureData: s.structureData,
      selectedRoom: s.selectedRoom,
      // The picked floor (null = all floors). Model rather than view state:
      // it scopes which of the building is on screen, and the renderer needs
      // it to know whether it is stacking floors at all (`ghostStoriesFor`).
      selectedStory: s.selectedStory,
      structureWalls: s.structureWalls,
    },
    viewState: {
      camera: { scale: s.scale, pan: s.pan, rotation: s.rotation },
      canvasSize: s.canvasSize,
      selectedItem: s.selectedItem,
      selectedFloorPlanMeasurement: s.selectedFloorPlanMeasurement,
      display: {
        showRoomLabels: s.showRoomLabels,
        showObjects: s.showObjects,
        shouldShowMeasurements: s.shouldShowMeasurements,
        debugMode: s.debugMode,
        measurementMode: s.measurementMode,
        dataSource: s.dataSource,
        showFixtures: s.showFixtures,
        showElectrical: s.showElectrical,
        showFurnishings: s.showFurnishings,
        showStaging: s.showStaging,
        showDemoedWalls: s.showDemoedWalls,
        showCollisionIndicator: s.showCollisionIndicator,
        measurementFontPx: s.measurementFontPx,
        showRoomWallsBold: s.showRoomWallsBold,
        // The inspected-electrical devices to draw, or null when the layer is
        // off — one value, so "on" can never be drawn with a stale list.
        inspectedElectrical: s.inspectedElectrical,
        debugPanelCollapsed: s.debugPanelCollapsed,
      },
    },
    editingState: {
      forceRedrawCounter: s.forceRedrawCounter,
      previewStructure: s.previewStructure,
      prePreviewStructure: s.prePreviewStructure,
      stretchBoundary: s.stretchBoundary,
      stretchRegion: s.stretchRegion,
      boundaryAxis: s.boundaryAxis,
      stretchBoundaryClampReason: s.stretchBoundaryClampReason,
      previewMap: s.previewMap,
      lockedKeys: s.lockedKeys,
      collisionTargets: s.collisionTargets,
      overlapWarningTargets: s.overlapWarningTargets,
      tempResizedObject: s.tempResizedObject,
      isResizing: s.isResizing,
      activeResizeHandle: s.activeResizeHandle,
      isDraggingObject: s.isDraggingObject,
      isDraggingWall: s.isDraggingWall,
      previewObject: s.previewObject,
      electricalMode: s.electricalMode,
      // Admin layer: show only the stated dimensions, plan lightened behind
      // them. This object is an ALLOWLIST — a key not named here never
      // reaches the renderer, and does so silently.
      showDimensionsOnly: s.showDimensionsOnly,
      wireShapeDrafts: s.wireShapeDrafts,
      selectedWiringRunId: s.selectedWiringRunId,
      showWiring: s.showWiring,
      connectionMode: s.connectionMode,
      connectionStart: s.connectionStart,
      takeOffOverlay: s.takeOffOverlay,
      roomHighlightTick: s.roomHighlightTick,
      roomDrawing: s.roomDrawing,
      cabinetRunDrawing: s.cabinetRunDrawing,
      recessedRowDrawing: s.recessedRowDrawing,
      wallChainDrawing: s.wallChainDrawing,
      // Callouts (notes). Admin-gated for now, so `showCallouts` is the
      // whole layer's switch, not just the tray's.
      showCallouts: s.showCallouts,
      selectedCalloutId: s.selectedCalloutId,
      calloutDrawing: s.calloutDrawing,
      appliancePreview: s.appliancePreview,
      runDrag: s.runDrag,
      isDraggingRun: s.isDraggingRun,
      vertexDelete: s.vertexDelete,
      vertexAdd: s.vertexAdd,
      vertexMove: s.vertexMove,
      objectInteractionMode: s.objectInteractionMode,
      cabinetEditScope: s.cabinetEditScope,
      isDraggingWallElement: s.isDraggingWallElement,
      grabHandlesEnabled: s.grabHandlesEnabled,
      dragPointer: s.dragPointer,
      activeWallHandle: s.activeWallHandle,
      originalWallState: s.originalWallState,
      roomHighlightStartedAt: s.roomHighlightStartedAt,
      disabledConstraints: s.disabledConstraints,
      highlightedElements: s.highlightedElements,
    },
    io: {
      canvasRef: s.canvasRef,
      measurementsRef: s.measurementsRef,
      wiringHitRef: s.wiringHitRef,
      calloutHitRef: s.calloutHitRef,
      fixedPinHitRef: s.fixedPinHitRef,
      collisionDebugRef: s.collisionDebugRef,
      dragRedirectTargetRef: s.dragRedirectTargetRef,
      selectedAtMouseDownRef: s.selectedAtMouseDownRef,
      canvasResizeHandlesFor: s.canvasResizeHandlesFor,
      drawTakeOffOverlay: s.drawTakeOffOverlay,
    },
  };
}

export function flattenFloorplanContracts(c) {
  const { model, viewState, editingState, io } = c;
  const { camera, display } = viewState;
  return {
    // model
    structureData: model.structureData,
    selectedRoom: model.selectedRoom,
    selectedStory: model.selectedStory,
    structureWalls: model.structureWalls,
    // viewState
    scale: camera.scale,
    pan: camera.pan,
    rotation: camera.rotation,
    canvasSize: viewState.canvasSize,
    selectedItem: viewState.selectedItem,
    selectedFloorPlanMeasurement: viewState.selectedFloorPlanMeasurement,
    showRoomLabels: display.showRoomLabels,
    showObjects: display.showObjects,
    shouldShowMeasurements: display.shouldShowMeasurements,
    debugMode: display.debugMode,
    measurementMode: display.measurementMode,
    dataSource: display.dataSource,
    showFixtures: display.showFixtures,
    showElectrical: display.showElectrical,
    showFurnishings: display.showFurnishings,
    showStaging: display.showStaging,
    showDemoedWalls: display.showDemoedWalls,
    showCollisionIndicator: display.showCollisionIndicator,
    measurementFontPx: display.measurementFontPx,
    showRoomWallsBold: display.showRoomWallsBold,
    inspectedElectrical: display.inspectedElectrical,
    debugPanelCollapsed: display.debugPanelCollapsed,
    // editingState
    forceRedrawCounter: editingState.forceRedrawCounter,
    previewStructure: editingState.previewStructure,
    prePreviewStructure: editingState.prePreviewStructure,
    stretchBoundary: editingState.stretchBoundary,
    stretchRegion: editingState.stretchRegion,
    boundaryAxis: editingState.boundaryAxis,
    stretchBoundaryClampReason: editingState.stretchBoundaryClampReason,
    previewMap: editingState.previewMap,
    lockedKeys: editingState.lockedKeys,
    collisionTargets: editingState.collisionTargets,
    overlapWarningTargets: editingState.overlapWarningTargets,
    tempResizedObject: editingState.tempResizedObject,
    isResizing: editingState.isResizing,
    activeResizeHandle: editingState.activeResizeHandle,
    isDraggingObject: editingState.isDraggingObject,
    isDraggingWall: editingState.isDraggingWall,
    previewObject: editingState.previewObject,
    electricalMode: editingState.electricalMode,
    showDimensionsOnly: editingState.showDimensionsOnly,
    wireShapeDrafts: editingState.wireShapeDrafts,
    selectedWiringRunId: editingState.selectedWiringRunId,
    showWiring: editingState.showWiring,
    connectionMode: editingState.connectionMode,
    connectionStart: editingState.connectionStart,
    takeOffOverlay: editingState.takeOffOverlay,
    roomHighlightTick: editingState.roomHighlightTick,
    roomDrawing: editingState.roomDrawing,
    cabinetRunDrawing: editingState.cabinetRunDrawing,
    recessedRowDrawing: editingState.recessedRowDrawing,
    wallChainDrawing: editingState.wallChainDrawing,
    showCallouts: editingState.showCallouts,
    selectedCalloutId: editingState.selectedCalloutId,
    calloutDrawing: editingState.calloutDrawing,
    appliancePreview: editingState.appliancePreview,
    runDrag: editingState.runDrag,
    isDraggingRun: editingState.isDraggingRun,
    vertexDelete: editingState.vertexDelete,
    vertexAdd: editingState.vertexAdd,
    vertexMove: editingState.vertexMove,
    objectInteractionMode: editingState.objectInteractionMode,
    cabinetEditScope: editingState.cabinetEditScope,
    isDraggingWallElement: editingState.isDraggingWallElement,
    grabHandlesEnabled: editingState.grabHandlesEnabled,
    dragPointer: editingState.dragPointer,
    activeWallHandle: editingState.activeWallHandle,
    originalWallState: editingState.originalWallState,
    roomHighlightStartedAt: editingState.roomHighlightStartedAt,
    disabledConstraints: editingState.disabledConstraints,
    highlightedElements: editingState.highlightedElements,
    // io
    canvasRef: io.canvasRef,
    measurementsRef: io.measurementsRef,
    wiringHitRef: io.wiringHitRef,
    calloutHitRef: io.calloutHitRef,
    fixedPinHitRef: io.fixedPinHitRef,
    collisionDebugRef: io.collisionDebugRef,
    dragRedirectTargetRef: io.dragRedirectTargetRef,
    selectedAtMouseDownRef: io.selectedAtMouseDownRef,
    canvasResizeHandlesFor: io.canvasResizeHandlesFor,
    drawTakeOffOverlay: io.drawTakeOffOverlay,
  };
}
